import React, { useState } from 'react';
import { Modal, Form, Input, InputNumber, Select } from 'antd';
import { SuggestedAction, MissingParam } from './types';

interface ActionInputDialogProps {
  visible: boolean;
  action: SuggestedAction | null;
  onSubmit: (params: Record<string, any>) => void;
  onCancel: () => void;
}

const ActionInputDialog: React.FC<ActionInputDialogProps> = ({
  visible,
  action,
  onSubmit,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // Merge user input with existing params
      const mergedParams = {
        ...action?.params,
        ...values,
      };

      onSubmit(mergedParams);
      form.resetFields();
      setSubmitting(false);
    } catch (error) {
      console.error('Form validation failed:', error);
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const renderField = (param: MissingParam) => {
    const inputType = param.input_config?.input_type || param.type;
    const label = param.input_config?.prompt || param.name;

    switch (inputType) {
      case 'number':
        return (
          <Form.Item
            key={param.name}
            name={param.name}
            label={label}
            rules={[{ required: true, message: `Please enter ${label}` }]}
          >
            <InputNumber style={{ width: '100%' }} placeholder={`Enter ${label}`} />
          </Form.Item>
        );
      case 'select':
        return (
          <Form.Item
            key={param.name}
            name={param.name}
            label={label}
            rules={[{ required: true, message: `Please select ${label}` }]}
          >
            <Select placeholder={`Select ${param.name}`} defaultOpen>
              {param.options?.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        );
      case 'text':
      default:
        return (
          <Form.Item
            key={param.name}
            name={param.name}
            label={label}
            rules={[
              { required: true, message: `Please enter ${label}` },
              { whitespace: true, message: `${label} cannot be empty` },
            ]}
          >
            <Input placeholder={`Enter ${label}`} />
          </Form.Item>
        );
    }
  };

  return (
    <Modal
      title={action?.label || 'Action Parameters'}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={submitting}
      okText="Execute"
      cancelText="Cancel"
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        {action?.missing_params?.map((param) => renderField(param))}
      </Form>
    </Modal>
  );
};

export default ActionInputDialog;
